import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Proposition {
  id: string;
  theme: string;
  domain: string;
  keywords: string[];
  textFr: string;
  textAr: string;
  partyIds: string[]; // Multiple parties
  status: 'en_attente' | 'valide' | 'rejete';
  submittedBy: string; // 'admin' ou partyAcronym
  createdAt: string;
}

export interface MatchBannerConfig {
  mode: 'banner' | 'button';
  placement: 'top' | 'bottom';
  textFr: string;
  textAr: string;
  buttonTextFr: string;
  buttonTextAr: string;
}

export interface MatchAnalytics {
  totalPlays: number;
  podiumCounts: Record<string, number>; // Combien de fois un parti a été dans le Top 3
}

export interface MatchDisclaimer {
  fr: string;
  ar: string;
}

interface MatchStore {
  isMatchEnabled: boolean;
  itemsPerSession: number;
  bannerConfig: MatchBannerConfig;
  propositions: Proposition[];
  analytics: MatchAnalytics;
  disclaimer: MatchDisclaimer;
  consolidatedParties: string[];
  
  toggleMatchEnabled: (enabled: boolean) => void;
  updateItemsPerSession: (num: number) => void;
  updateBannerConfig: (config: Partial<MatchBannerConfig>) => void;
  updateDisclaimer: (fr: string, ar: string) => Promise<void>;
  fetchMatchData: () => Promise<void>;
  addProposition: (prop: Omit<Proposition, 'id' | 'createdAt'>) => Promise<void>;
  updatePropositionStatus: (id: string, status: Proposition['status']) => Promise<void>;
  updateProposition: (id: string, data: Partial<Proposition>) => Promise<void>;
  deleteProposition: (id: string) => Promise<void>;
  recordPlay: (podium: string[]) => Promise<void>;
  resetAnalytics: () => Promise<void>;
  togglePartyAcceptance: (propId: string, partyAcronym: string) => Promise<void>;
  publishPartyChoices: (partyAcronym: string) => Promise<void>;
}

import { getApiUrl } from "@/utils/apiConfig";
const API_URL = getApiUrl();

export const useMatchStore = create<MatchStore>()((set, get) => ({
  isMatchEnabled: true,
  itemsPerSession: 5,
  bannerConfig: {
    mode: 'banner',
    placement: 'top',
    textFr: "Tu ne sais pas pour qui voter ?",
    textAr: "ماعرفتيش لمن تصوت؟",
    buttonTextFr: "Faire le test en 1 minute",
    buttonTextAr: "دير الاختبار فدقيقة",
  },
  analytics: {
    totalPlays: 0,
    podiumCounts: {}
  },
  disclaimer: {
    fr: "🎯 Ce module est un jeu de découverte civique dont les données s'affinent de jour en jour au rythme des adhésions. Prenez-le comme un point de départ pour explorer les programmes, pas comme une science exacte !",
    ar: "🎯 هذه اللعبة هي تجربة لاكتشاف البرامج، يتم تحديث بياناتها باستمرار. خذها كنقطة انطلاق وليس كعلم دقيق!"
  },
  consolidatedParties: [],
  propositions: [],
  toggleMatchEnabled: (enabled) => set({ isMatchEnabled: enabled }),
  updateItemsPerSession: (num) => set({ itemsPerSession: Math.max(5, Math.min(20, num)) }),
  updateBannerConfig: (config) => set((state) => ({ bannerConfig: { ...state.bannerConfig, ...config } })),
  
  fetchMatchData: async () => {
    try {
      const res = await fetch(`${API_URL}/match.php`);
      if (res.ok) {
        const data = await res.json();
        if (data.propositions) set({ propositions: data.propositions });
        if (data.analytics) set({ analytics: data.analytics });
        if (data.disclaimer) set({ disclaimer: data.disclaimer });
        if (data.consolidatedParties) set({ consolidatedParties: data.consolidatedParties });
      }
    } catch (e) {
      console.error("Failed to fetch match data", e);
    }
  },

  updateDisclaimer: async (fr, ar) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/match.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'update_disclaimer', disclaimerFr: fr, disclaimerAr: ar })
      });
      if (!res.ok) throw new Error("Failed to update disclaimer");
      set({ disclaimer: { fr, ar } });
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  addProposition: async (prop) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/match.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'add', ...prop })
      });
      if (!res.ok) throw new Error("Failed to add proposition");
      await get().fetchMatchData();
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  updatePropositionStatus: async (id, status) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/match.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'update_status', id, status })
      });
      if (!res.ok) throw new Error("Failed to update status");
      await get().fetchMatchData();
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  updateProposition: async (id, data) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/match.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'update', id, ...data })
      });
      if (!res.ok) throw new Error("Failed to update proposition");
      await get().fetchMatchData();
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  deleteProposition: async (id) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/match.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'delete', id })
      });
      if (!res.ok) throw new Error("Failed to delete proposition");
      await get().fetchMatchData();
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  recordPlay: async (podium) => {
    try {
      const res = await fetch(`${API_URL}/match.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'record_play', podium })
      });
      if (!res.ok) throw new Error("Failed to record play");
      set((state) => {
        const newCounts = { ...state.analytics.podiumCounts };
        podium.forEach(party => {
          newCounts[party] = (newCounts[party] || 0) + 1;
        });
        return {
          analytics: {
            totalPlays: state.analytics.totalPlays + 1,
            podiumCounts: newCounts
          }
        };
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  resetAnalytics: async () => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/match.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'reset_analytics' })
      });
      if (!res.ok) throw new Error("Failed to reset analytics");
      set({ analytics: { totalPlays: 0, podiumCounts: {} } });
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  togglePartyAcceptance: async (propId, partyAcronym) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/match.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'toggle_party_acceptance', propId, partyAcronym })
      });
      if (!res.ok) throw new Error("Failed to toggle party acceptance");
      set((state) => ({
        propositions: state.propositions.map(p => {
          if (p.id === propId) {
            const partyIds = p.partyIds.includes(partyAcronym) 
              ? p.partyIds.filter(id => id !== partyAcronym)
              : [...p.partyIds, partyAcronym];
            return { ...p, partyIds };
          }
          return p;
        })
      }));
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  publishPartyChoices: async (partyAcronym) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/match.php`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'consolidate_party', partyAcronym })
      });
      if (!res.ok) throw new Error("Failed to publish choices");
      set((state) => ({ consolidatedParties: [...new Set([...state.consolidatedParties, partyAcronym])] }));
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
}));

if (typeof window !== 'undefined') {
  useMatchStore.getState().fetchMatchData();
}
